// Anzeige-Bausteine für das, was vor dem Gespräch schon über eine Person
// bekannt ist: die Notiz aus dem Altsystem und die Wiedervorlagen.
//
// Bewusst gemeinsam für Kundenakte und Anruffenster — beide zeigten dieselbe
// Sache vorher gar nicht bzw. unterschiedlich (Lilly, 25.08.2026). Wer im Power
// Dialer telefoniert, soll denselben Text sehen wie in der Akte, ohne
// zurückzuschalten.

import { useState } from 'react';
import { CalendarClock, Clock, UserRoundCog, X } from 'lucide-react';
import { faelligkeit, istOffen, statusText, type WiedervorlageKurz } from '../../lib/kundenKontext';
import { MitarbeiterAuswahl, OFFEN_FUER_ALLE } from '../MitarbeiterAuswahl';

/**
 * Notiz aus der Zeit vor der Umstellung (unveränderlich, ohne Verfasser und
 * ohne Datum — genau das sagt die Überschrift auch).
 *
 * Lilly, 19. und 25.08.2026: „Alle Notizen aus dem alten System wurden nicht
 * übernommen." Übernommen wurden sie sehr wohl — sie standen nur nicht dort,
 * wo man sie braucht. Betrifft 33.418 Personen; gerade die mit fertigem
 * Qualicall, die erst nach den Ferien einen Termin wollten, hängen daran.
 */
export function AltNotiz({ text, kompakt = false }: { text: string | null | undefined; kompakt?: boolean }) {
  const inhalt = (text ?? '').trim();
  if (!inhalt) return null;
  return (
    <div style={{ marginTop: kompakt ? 0 : 12 }}>
      <div style={{
        fontSize: kompakt ? 11 : 12, color: 'var(--text-secondary)', textTransform: 'uppercase',
        letterSpacing: '0.04em', marginBottom: kompakt ? 4 : 6, fontWeight: kompakt ? 700 : 400,
      }}>
        Notiz aus dem Altsystem
        <span style={{ textTransform: 'none', fontWeight: 400 }}>
          {' '}· Stand vor der Umstellung, nicht mehr änderbar
        </span>
      </div>
      <div style={{
        fontSize: 13, padding: '8px 10px', background: 'var(--background)',
        borderRadius: 6, borderLeft: '3px solid var(--text-muted)',
        color: 'var(--text-secondary)', whiteSpace: 'pre-wrap',
        maxHeight: kompakt ? 120 : undefined, overflowY: kompakt ? 'auto' : undefined,
      }}>
        {inhalt}
      </div>
    </div>
  );
}

/**
 * Eine Wiedervorlage: wann sie fällig ist und was dazu notiert wurde — das
 * Paar, das vorher nur in der Wiedervorlagen-Liste zu sehen war.
 *
 * Mit `onZuweisen` ist die Zuständigkeit hier auch änderbar (Kundenakte).
 * Ohne den Vorgang bleibt die Zeile reine Anzeige — im Anruffenster soll
 * während des Gesprächs nichts umgehängt werden können.
 */
export function WiedervorlageZeile({ w, jetzt, eigeneId, onZuweisen }: {
  w: WiedervorlageKurz;
  jetzt?: Date;
  /** Eigene User-ID — steht in der Auswahl als „Mir zuweisen" an erster Stelle. */
  eigeneId?: string | null;
  /** Gesetzt = die Zuständigkeit lässt sich an dieser Zeile übergeben. */
  onZuweisen?: (w: WiedervorlageKurz, agentId: string) => void | Promise<void>;
}) {
  const offen = istOffen(w);
  const f = faelligkeit(w.due_at, jetzt);
  const ton = !offen ? 'var(--text-muted)' : f.ueberfaellig ? 'var(--danger)' : f.heute ? 'var(--warning)' : 'var(--rot-dunkel)';
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 6, background: 'var(--background)',
      borderLeft: `3px solid ${ton}`, opacity: offen ? 1 : 0.7,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
        <Clock size={13} style={{ color: ton, flexShrink: 0 }} />
        <strong style={{ color: ton }}>{f.text}</strong>
        {f.ueberfaellig && offen && <span style={chip('var(--danger)')}>überfällig</span>}
        {offen && w.in_progress && <span style={chip('var(--ton-gelb)')}>in Bearbeitung</span>}
        {!offen && <span style={chip('var(--text-muted)')}>{statusText(w)}</span>}
        {w.source === 'system' && <span style={chip('var(--text-muted)')}>automatisch</span>}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
          {/* Erledigte/abgebrochene bleiben Anzeige: umhängen würde nur eine
              Liste verändern, in der niemand mehr etwas zu tun hat. */}
          {onZuweisen && offen
            ? <Zustaendig w={w} eigeneId={eigeneId} onZuweisen={onZuweisen} />
            : w.agent_name ? `für ${w.agent_name}` : 'offen für alle'}
        </span>
      </div>
      {/* Der eigentliche Punkt der Rückmeldung: der Text, den jemand beim
          Anlegen geschrieben hat. */}
      <div style={{
        marginTop: 4, fontSize: 13,
        color: w.note ? 'var(--text-primary)' : 'var(--text-muted)',
        whiteSpace: 'pre-wrap',
      }}>
        {w.note?.trim() || 'ohne Notiz'}
      </div>
    </div>
  );
}

/**
 * Nur die offenen Wiedervorlagen, kompakt — fürs Anruffenster, wo der Platz
 * knapp ist und erledigte niemanden mehr interessieren.
 */
export function OffeneWiedervorlagen({ rows, jetzt }: { rows: WiedervorlageKurz[]; jetzt?: Date }) {
  const offen = rows.filter(istOffen);
  if (offen.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <CalendarClock size={12} /> Offene Wiedervorlage{offen.length > 1 ? 'n' : ''}
      </div>
      {offen.map((w) => <WiedervorlageZeile key={w.id} w={w} jetzt={jetzt} />)}
    </div>
  );
}

/**
 * „Zuständig" an einer Wiedervorlage in der Kundenakte: Name plus ein Knopf,
 * um sie zu übergeben — mir, einer Kollegin / einem Kollegen oder zurück an
 * „offen für alle".
 *
 * Sharina, 28.08.2026: „Ich bekomme manchmal Mails mit Erreichbarkeitsinfos
 * und würde dann eine Wiedervorlage für die Mädels anlegen — dafür bräuchte
 * ich die Auswahl auch, wenn ich nur auf den Kunden und dann Wiedervorlagen
 * gehe." Bisher musste man dafür in die Wiedervorlagen-Liste wechseln und die
 * Zeile dort wiederfinden.
 *
 * Die Auswahl erscheint erst auf Klick — wie in der Wiedervorlagen-Liste. Ein
 * Dauer-Dropdown in jeder Zeile würde die Akte unruhig machen.
 */
function Zustaendig({ w, eigeneId, onZuweisen }: {
  w: WiedervorlageKurz;
  eigeneId?: string | null;
  onZuweisen: (w: WiedervorlageKurz, agentId: string) => void | Promise<void>;
}) {
  const [aendern, setAendern] = useState(false);

  if (aendern) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <MitarbeiterAuswahl
          wert={w.agent_id ?? OFFEN_FUER_ALLE}
          eigeneId={eigeneId}
          onChange={(wert) => {
            setAendern(false);
            void onZuweisen(w, wert);
          }}
          aria-label="Zuständigkeit ändern"
          style={{
            padding: '3px 6px', borderRadius: 6, fontSize: 12, maxWidth: 180,
            border: '1px solid var(--border-color)', background: 'var(--surface)',
          }}
        />
        <button onClick={() => setAendern(false)} title="Abbrechen" style={knopf}>
          <X size={12} />
        </button>
      </span>
    );
  }

  // „von X zugewiesen" nur, wenn es überhaupt eine Zuweisung gibt UND jemand
  // anderes sie gemacht hat — „für Kristin, von Kristin zugewiesen" ist
  // Rauschen.
  const vonWem = w.agent_id && w.created_by && w.created_by !== w.agent_id
    ? (w.created_by === eigeneId ? 'dir' : w.created_by_name)
    : null;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {w.agent_name ? `für ${w.agent_name}` : 'offen für alle'}
      {vonWem && <span style={{ color: 'var(--text-muted)' }}>· von {vonWem} zugewiesen</span>}
      <button
        onClick={() => setAendern(true)}
        title="Zuständigkeit ändern — mir, einer Kollegin / einem Kollegen oder offen für alle"
        aria-label="Zuständigkeit ändern"
        style={knopf}
      ><UserRoundCog size={12} /></button>
    </span>
  );
}

const knopf: React.CSSProperties = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: 'var(--text-secondary)', padding: 2, display: 'inline-flex', alignItems: 'center',
};

function chip(farbe: string): React.CSSProperties {
  return {
    fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
    color: farbe, border: `1px solid ${farbe}`,
    background: `color-mix(in srgb, ${farbe} 10%, transparent)`,
  };
}
